/**
 * Pure helpers for the Home Redesign "You" lens + the Company ⇄ You LensSwitch.
 *
 * Everything here is pure + total (no React, no DOM beyond a guarded localStorage
 * read) so it can be unit-tested from source — vitest is a deploy gate.
 *
 * Money note: every *Cents value from /me/overview is INTEGER cents
 * (PersonalOverview.income.*, byEntity[].*). OwnerDistribution.amount_cents is a
 * pg BIGINT that serializes as string|number — callers should Number() it first.
 */

/** Format integer cents → "$1,234" (no cents shown ≥ $1,000) / "$12.34". null → '—'. */
export function fmtCents(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Math.abs(dollars) >= 1000 ? 0 : 2,
    maximumFractionDigits: Math.abs(dollars) >= 1000 ? 0 : 2,
  }).format(dollars);
}

/** Format a 0..1 fraction → whole-percent string, e.g. 0.732 → "73%". */
export function fmtPct(fraction: number | null | undefined): string {
  if (fraction == null || !Number.isFinite(fraction)) return '—';
  return `${Math.round(fraction * 100)}%`;
}

/**
 * The You-lens keep-metric (v1): after-tax kept share of total income.
 * keptPct = (totalIncome − totalTax) / totalIncome, clamped to [0, 1].
 * totalTaxCents may be null (tax projection unavailable) → returns null.
 */
export function afterTaxKeptPct(
  totalIncomeCents: number,
  totalTaxCents: number | null | undefined,
): number | null {
  if (!Number.isFinite(totalIncomeCents) || totalIncomeCents <= 0) return null;
  if (totalTaxCents == null || !Number.isFinite(totalTaxCents)) return null;
  const kept = (totalIncomeCents - totalTaxCents) / totalIncomeCents;
  return Math.max(0, Math.min(1, kept));
}

/** Confirmed share of the expected-revenue pipeline (0..1). 0 when nothing upcoming. */
export function confirmedShare(confirmedCents: number, scheduledCents: number): number {
  const total = (confirmedCents || 0) + (scheduledCents || 0);
  if (total <= 0) return 0;
  return Math.max(0, Math.min(1, (confirmedCents || 0) / total));
}

// ---- LensSwitch persistence (Company ⇄ You) ----

export type Lens = 'company' | 'you';

/** localStorage key for the last-chosen lens (parity with `nexus_nav_layout`). */
export const LENS_KEY = 'nexus_lens';

/** Derive the active lens from the current pathname. `/me*` ⇒ You, else Company. */
export function lensForPath(pathname: string): Lens {
  return pathname === '/me' || pathname.startsWith('/me/') ? 'you' : 'company';
}

/** The route a lens points at. */
export function lensRoute(lens: Lens): string {
  return lens === 'you' ? '/me' : '/';
}

/** Record the chosen lens (best-effort; never throws). */
export function writeLens(lens: Lens): void {
  try {
    localStorage.setItem(LENS_KEY, lens);
  } catch {
    /* private mode / quota — non-fatal */
  }
}

/** Read the recorded lens, defaulting to 'company'. */
export function readLens(): Lens {
  try {
    return localStorage.getItem(LENS_KEY) === 'you' ? 'you' : 'company';
  } catch {
    return 'company';
  }
}
